import type { AxiosResponseHeaders, RawAxiosRequestHeaders } from 'axios';
import axios from 'axios';

import { authInterceptor } from './interceptors/AuthInterceptor';
import { localeInterceptor } from './interceptors/LocaleInterceptor';

const TIMEOUT_IN_MILLIS = 60000;

type Body = Record<string, unknown> | FormData | object | undefined;

const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL,
  timeout: TIMEOUT_IN_MILLIS,
  headers: { Accept: 'application/json' },
  paramsSerializer: { indexes: null },
});

api.interceptors.request.use(authInterceptor);
api.interceptors.request.use(localeInterceptor);

export interface Response<T> {
  data: T;
  headers?: AxiosResponseHeaders;
  request: unknown;
}

type QueryParams = Record<string, string | number | boolean | string[]>;

interface RequestConfig<B = Body> {
  body?: B;
  params?: QueryParams;
  endpoint: string;
  signal?: AbortSignal;
  headers?: RawAxiosRequestHeaders;
  timeout?: number;
  responseType?:
    | 'arraybuffer'
    | 'blob'
    | 'document'
    | 'json'
    | 'text'
    | 'stream';
}

interface DownloadConfig {
  endpoint: string;
}

interface DownloadBlob {
  blob: Blob;
  fileName: string;
}

class AxiosClient {
  get<TResponseData>({
    endpoint,
    ...config
  }: RequestConfig): Promise<Response<TResponseData>> {
    return api.get(endpoint, config);
  }

  post<TResponseData, TBody extends Body>({
    body,
    endpoint,
    ...config
  }: RequestConfig<TBody>): Promise<Response<TResponseData>> {
    return api.post(endpoint, body, config);
  }

  put<TResponseData, TBody extends Body>({
    body,
    endpoint,
    ...config
  }: RequestConfig<TBody>): Promise<Response<TResponseData>> {
    return api.put(endpoint, body, config);
  }

  delete<TBody extends Body, TResponseData = void>({
    endpoint,
    body,
    ...config
  }: RequestConfig<TBody>): Promise<Response<TResponseData>> {
    return api.delete(endpoint, {
      data: body,
      ...config,
    });
  }

  patch<TResponseData, TBody extends Body>({
    body,
    endpoint,
    ...config
  }: RequestConfig<TBody>): Promise<Response<TResponseData>> {
    return api.patch(endpoint, body, config);
  }

  head<TResponseData>({
    endpoint,
    ...config
  }: RequestConfig): Promise<Response<TResponseData>> {
    return api.head(endpoint, config);
  }

  download({ endpoint }: DownloadConfig): void {
    const link = document.createElement('a');
    link.href = endpoint;

    document.body.appendChild(link);

    link.click();
    link.remove();
  }

  downloadBlob({ blob, fileName }: DownloadBlob): void {
    const url = URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;

    document.body.appendChild(link);
    link.click();

    URL.revokeObjectURL(url);

    link.remove();
  }
}

export const Client = new AxiosClient();
